import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { detectedTimezone } from './datetime';
import { csrfCookie, ensureCsrf, sessionGeneration, currentSessionId } from './sessionState';
import { nativeFile } from './androidApp';
import { toast } from 'sonner';
import i18n, { errorText } from './i18n';

const SITE_EVENT = 'maldhowr-site-changed';
const siteChannel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('maldhowr-site') : null;

export function watchSiteChanges(handler) {
  window.addEventListener(SITE_EVENT, handler);
  window.addEventListener('focus', handler);
  siteChannel?.addEventListener('message', handler);
  return () => {
    window.removeEventListener(SITE_EVENT, handler);
    window.removeEventListener('focus', handler);
    siteChannel?.removeEventListener('message', handler);
  };
}

export const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });

api.interceptors.request.use(async cfg => {
  cfg.sessionGeneration = sessionGeneration();
  cfg.authenticated = !!currentSessionId();
  if (['put', 'post', 'patch', 'delete'].includes(cfg.method)) {
    cfg.headers['X-CSRF-Token'] = csrfCookie() || await ensureCsrf(api);
    if (currentSessionId() && !cfg.headers['X-Session-Id']) cfg.headers['X-Session-Id'] = currentSessionId();
  }
  cfg.headers['X-Timezone'] = detectedTimezone();
  cfg.headers['Accept-Language'] = i18n.resolvedLanguage || 'en';
  return cfg;
});

api.interceptors.response.use(response => {
  const { method, url } = response.config;
    if (['put', 'post', 'patch', 'delete'].includes(method) && (url.startsWith('/owner/settings/') || url.startsWith('/owner/pages') || url.startsWith('/owner/cms/') || url.startsWith('/customers') || url === '/shop/country' || url === '/owner/currencies' || url.endsWith('/staff-allowance') || url.endsWith('/permissions'))) {
    window.dispatchEvent(new Event(SITE_EVENT));
    siteChannel?.postMessage('refresh');
  }
  if (['put', 'post', 'patch', 'delete'].includes(method) && /^\/(portal\/(qr-payments|payment-requests|transfer)|merchant\/qr|transactions|loans)(\/|$)/.test(url)) {
    window.dispatchEvent(new Event(SITE_EVENT));
    siteChannel?.postMessage('refresh');
  }
  return response;
}, error => {
  if (error.response?.status === 401 && error.config?.authenticated && error.config.sessionGeneration === sessionGeneration()
      && !['/auth/sign-in', '/auth/login', '/auth/customer-login', '/auth/google/session', '/auth/pin/unlock'].includes(error.config.url)) {
    window.dispatchEvent(new CustomEvent('maldhowr-session-expired', { detail: error.response.data?.detail || 'Session expired' }));
  }
  if (error.response?.status === 503 && error.response?.data?.maintenance) window.dispatchEvent(new Event('maldhowr-maintenance'));
  return Promise.reject(error);
});

export function errMsg(e) {
  const d = e?.response?.data?.detail;
  if (!d) return errorText(e?.message || 'Something went wrong');
  if (typeof d === 'string') return errorText(d);
  if (Array.isArray(d)) return d.map(x => errorText(x?.msg?.replace(/^Value error, /, '') || 'Check the entered details')).join(' ');
  return errorText(d.msg || String(d));
}

export function useApi(path) {
  const requestId = useRef(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reload = useCallback((options = {}) => {
    const id = ++requestId.current;
    if (!options?.quiet) setLoading(true);
    return api.get(path).then(r => { if (id === requestId.current) { setData(r.data); setError(null); } }).catch(e => { if (id === requestId.current) { setData(null); setError(errMsg(e)); } }).finally(() => { if (id === requestId.current) setLoading(false); });
  }, [path]);
  useEffect(() => { setData(null); reload(); return () => { requestId.current += 1; }; }, [reload]);
  useEffect(() => {
    if (!/^\/(portal\/(me|transactions|statement)|dashboard|transactions|statement|balances|reports\/balance-summary)(\?|$)/.test(path)) return;
    const refresh = () => { if (!document.hidden) reload({ quiet: true }); };
    const stop = watchSiteChanges(refresh);
    const timer = setInterval(refresh, 5000);
    return () => { stop(); clearInterval(timer); };
  }, [path, reload]);
  return { data, loading, error, reload, setData };
}

export async function downloadCsv(filename, lines) {
  const csv = lines.join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv' });
  try { if (await nativeFile('download', blob, filename)) return; }
  catch (failure) { if (failure.name !== 'AbortError') toast.error(failure.message || 'Statement could not be saved.'); return; }
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

export function csvCell(v) {
  let s = String(v ?? '');
  if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
